import { useState } from "react"

import { useGetInventoryPalletGroups } from "../../../hooks"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { InventoryPalletGroup } from "@/graphql/codegen/graphql"
import { InventoryPalletGroupsSortField, SortingOrder } from "@/graphql/codegen/graphql"
import useDebounce from "@/utils/useDebounce"

interface UseTransferPalletSelectionParams {
  productExternalSystemId: string
  warehouseAreaExternalSystemId: string
}

interface UseTransferPalletSelectionReturn {
  palletGroups: InventoryPalletGroup[]
  isFetching: boolean
  sortBy: InventoryPalletGroupsSortField
  sortDirection: SortingOrder
  aisleCodes: string[]
  handleSearchChange: (value: string) => void
  handleSortChange: (field: InventoryPalletGroupsSortField, direction: SortingOrder) => void
  handleAisleCodesChange: (aisleCodes: string[]) => void
}

export const useTransferPalletSelection = ({
  productExternalSystemId,
  warehouseAreaExternalSystemId,
}: UseTransferPalletSelectionParams): UseTransferPalletSelectionReturn => {
  const [search, setSearch] = useState("")
  const [sortBy, setSortBy] = useState<InventoryPalletGroupsSortField>(InventoryPalletGroupsSortField.ExpirationDate)
  const [sortDirection, setSortDirection] = useState<SortingOrder>(SortingOrder.Asc)
  const [aisleCodes, setAisleCodes] = useState<string[]>([])

  const debouncedSearch = useDebounce(search, DEBOUNCE_DELAY_MS)

  const { palletGroups, isFetching } = useGetInventoryPalletGroups({
    productExternalSystemId,
    warehouseAreaExternalSystemId,
    search: debouncedSearch || undefined,
    sortBy,
    sortDirection,
    aisleCodes,
  })

  const handleSearchChange = (value: string) => setSearch(value)

  const handleSortChange = (field: InventoryPalletGroupsSortField, direction: SortingOrder) => {
    setSortBy(field)
    setSortDirection(direction)
  }

  const handleAisleCodesChange = (value: string[]) => setAisleCodes(value)

  return {
    palletGroups,
    isFetching,
    sortBy,
    sortDirection,
    aisleCodes,
    handleSearchChange,
    handleSortChange,
    handleAisleCodesChange,
  }
}
